import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Pencil, Prohibit, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { claimsApi } from '../../services/api';
import { formatCurrency, formatDate } from '../../lib/utils';

// Standard CMS-1500 place-of-service codes used in the EDI 837P SV1 segment.
// Inlined here (previously lived in ./constants alongside dead wizard STEPS)
// so we can drop the otherwise-empty file.
const PLACE_OF_SERVICE = [
  { value: '12', label: '12 - Home' },
  { value: '11', label: '11 - Office' },
  { value: '99', label: '99 - Other' },
];

/**
 * Side-by-side correction / void dialog. Shows the original (read-only)
 * on the left and the new claim being created on the right. Edited fields
 * highlight in amber so the biller sees exactly what's changing before
 * submitting to ODM as a corrected (CLM05-3='7') or void (='8') claim.
 *
 * Props:
 *   - open (bool)
 *   - mode ('correct' | 'void')
 *   - claim (full claim doc fetched on open) | null
 *   - onClose ()
 *   - onSuccess (newClaim) — called after creation; parent navigates / refreshes
 */

// ── per-row labelling helpers ────────────────────────────────────────────

const fieldFormatter = {
  service_date: (v) => (v ? formatDate(v) : '—'),
  estimated_reimbursement: (v) => (v == null ? '—' : formatCurrency(v)),
  units: (v) => (v == null || v === '' ? '—' : String(v)),
};

const formatValue = (key, v) => {
  if (fieldFormatter[key]) return fieldFormatter[key](v);
  if (v == null || v === '') return '—';
  return String(v);
};

// Editable fields shown on the right pane. Order matters (top → bottom UI).
const EDITABLE_FIELDS = [
  { key: 'service_date', label: 'Service Date', type: 'date' },
  { key: 'time_in', label: 'Time In', type: 'time' },
  { key: 'time_out', label: 'Time Out', type: 'time' },
  { key: 'service_code', label: 'HCPCS Code', type: 'text' },
  { key: 'modifier_1', label: 'Modifier 1', type: 'text' },
  { key: 'modifier_2', label: 'Modifier 2', type: 'text' },
  { key: 'units', label: 'Units', type: 'number' },
  { key: 'estimated_reimbursement', label: 'Charge ($)', type: 'number', step: '0.01' },
  { key: 'place_of_service', label: 'Place of Service', type: 'select', options: PLACE_OF_SERVICE },
  { key: 'diagnosis_code', label: 'Primary Dx (ICD-10)', type: 'text' },
  { key: 'diagnosis_code_2', label: 'Secondary Dx', type: 'text' },
  { key: 'rendering_provider_npi', label: 'Rendering Provider NPI', type: 'text' },
  { key: 'referring_provider_npi', label: 'Referring Provider NPI', type: 'text' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
];

// Read-only summary block at the top of each pane.
const SUMMARY_FIELDS = [
  { key: 'claim_number', label: 'Claim #' },
  { key: 'patient_name', label: 'Patient' },
  { key: 'status', label: 'Status' },
];

const PaneHeader = ({ tone, title, subtitle, badge }) => (
  <div className={`flex items-center justify-between p-3 rounded-t-lg border-b ${
    tone === 'left' ? 'bg-slate-50 border-slate-200' : 'bg-indigo-50 border-indigo-200'
  }`}>
    <div>
      <h3 className={`text-sm font-semibold ${tone === 'left' ? 'text-slate-700' : 'text-indigo-900'}`}>{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    {badge && (
      <span className={`text-[10px] font-mono px-2 py-1 rounded ${
        tone === 'left' ? 'bg-slate-200 text-slate-700' : 'bg-indigo-200 text-indigo-900'
      }`}>{badge}</span>
    )}
  </div>
);

const ReadOnlyRow = ({ label, value, highlighted }) => (
  <div className={`grid grid-cols-3 gap-2 py-1.5 px-3 text-sm border-b border-slate-100 ${
    highlighted ? 'bg-amber-50' : ''
  }`}>
    <span className="text-slate-500 col-span-1">{label}</span>
    <span className="col-span-2 text-slate-900 font-mono text-xs">{value}</span>
  </div>
);

const FieldEditor = ({ field, value, originalValue, onChange, disabled }) => {
  const changed = value !== originalValue && !(value == null && originalValue == null);
  const wrapperClass = `grid grid-cols-3 gap-2 py-1.5 px-3 border-b border-slate-100 ${
    changed ? 'bg-amber-50' : ''
  }`;
  const id = `cv-field-${field.key}`;

  let input;
  if (field.type === 'select') {
    input = (
      <select
        id={id}
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        className="w-full h-8 px-2 text-xs border border-slate-300 rounded font-mono"
        data-testid={`cv-input-${field.key}`}
      >
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  } else if (field.type === 'textarea') {
    input = (
      <Textarea
        id={id}
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        rows={2}
        className="text-xs font-mono"
        data-testid={`cv-input-${field.key}`}
      />
    );
  } else {
    input = (
      <Input
        id={id}
        type={field.type}
        step={field.step}
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => onChange(field.key, field.type === 'number' ? e.target.value : e.target.value)}
        className="h-8 text-xs font-mono"
        data-testid={`cv-input-${field.key}`}
      />
    );
  }

  return (
    <div className={wrapperClass}>
      <Label htmlFor={id} className="text-xs text-slate-500 col-span-1 self-center">{field.label}</Label>
      <div className="col-span-2">{input}</div>
    </div>
  );
};

// Coerce form-input strings into typed values matching the original schema.
const coerceUpdates = (raw, original) => {
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const orig = original[k];
    if (v === '' || v == null) {
      if (orig != null && orig !== '') out[k] = ''; // user cleared it
      continue;
    }
    if (typeof orig === 'number') {
      const n = Number(v);
      if (!Number.isNaN(n)) out[k] = n;
    } else {
      out[k] = v;
    }
  }
  return out;
};

// Diff: which keys of `next` actually differ from `original`?
const diffKeys = (next, original) => {
  const changed = [];
  for (const k of Object.keys(next)) {
    const a = next[k];
    const b = original[k];
    if (a !== b && !(a == null && b == null) && !(a === '' && b == null)) changed.push(k);
  }
  return changed;
};

export const CorrectVoidDialog = ({ open, mode, claim, onClose, onSuccess }) => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isVoid = mode === 'void';
  const titleIcon = isVoid ? <Prohibit size={20} className="text-red-600" /> : <Pencil size={20} className="text-indigo-600" />;
  const titleText = isVoid ? 'Void Claim' : 'Correct Claim';
  const ctaText = isVoid ? 'Confirm Void → Create Draft' : 'Save Correction → Create Draft';

  // Hydrate form with the original claim values whenever a new claim is loaded.
  useEffect(() => {
    if (!claim) return;
    const seed = {};
    EDITABLE_FIELDS.forEach((f) => { seed[f.key] = claim[f.key] ?? ''; });
    setForm(seed);
  }, [claim?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changedFields = useMemo(() => (claim ? diffKeys(form, claim) : []), [form, claim]);

  if (!claim) return null;

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let res;
      if (isVoid) {
        res = await claimsApi.void(claim.id);
        toast.success(`Void created — new draft ${res.data.claim_number}`);
      } else {
        // Only send keys the user actually changed — protects against empty-string
        // clobbering of values that were already empty in the original.
        const onlyChanged = Object.fromEntries(
          changedFields.map((k) => [k, form[k]]),
        );
        const updates = coerceUpdates(onlyChanged, claim);
        res = await claimsApi.correct(claim.id, updates);
        const n = changedFields.length;
        toast.success(`Corrected — new draft ${res.data.claim_number}${n ? ` (${n} field${n > 1 ? 's' : ''} changed)` : ''}`);
      }
      onSuccess?.(res.data);
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail || `Failed to ${isVoid ? 'void' : 'correct'} claim`;
      toast.error(typeof detail === 'string' ? detail : 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="correct-void-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {titleIcon}
            <span>{titleText}</span>
            <span className="text-xs font-mono text-slate-500 ml-2">→ ODM CLM05-3 = {isVoid ? '8' : '7'}</span>
          </DialogTitle>
          <DialogDescription>
            {isVoid ? (
              <>This will create a new <span className="font-semibold text-red-700">VOID claim (Draft)</span> referencing the original ICN <span className="font-mono">{claim.submitted_icn || '(none)'}</span> via REF*F8. Per X12 5010 X222A1 §3, a void must mirror the original — fields are read-only.</>
            ) : (
              <>Edit any field below. We'll create a new <span className="font-semibold text-indigo-700">CORRECTED claim (Draft)</span> referencing the original ICN <span className="font-mono">{claim.submitted_icn || '(none)'}</span> via REF*F8. Modified fields highlight in amber.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {!claim.submitted_icn && (
          <div className="mt-2 p-3 rounded border bg-amber-50 border-amber-200">
            <div className="flex items-start gap-2 text-xs">
              <Warning size={14} className="text-amber-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-900">No original ICN on file for this claim.</p>
                <p className="text-amber-800 mt-0.5">
                  This claim was submitted before AZAI started stamping ICNs (April 25 onward). ODM requires REF*F8 to link the corrected/void claim back to the original — you'll get a 400 error on submit. Contact ODM support to retrieve the ICN and update this claim manually, or use "Copy Claim (Recycle)" instead to bill the same service on a fresh date.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Top summary banner */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          {/* LEFT — original */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <PaneHeader
              tone="left"
              title="Original Claim"
              subtitle="Already submitted to ODM (read-only)"
              badge={claim.claim_number}
            />
            <div className="bg-white">
              {SUMMARY_FIELDS.map((f) => (
                <ReadOnlyRow key={f.key} label={f.label} value={formatValue(f.key, claim[f.key])} />
              ))}
              {EDITABLE_FIELDS.map((f) => (
                <ReadOnlyRow
                  key={f.key}
                  label={f.label}
                  value={formatValue(f.key, claim[f.key])}
                  highlighted={!isVoid && changedFields.includes(f.key)}
                />
              ))}
            </div>
          </div>

          {/* RIGHT — new */}
          <div className={`border rounded-lg overflow-hidden ${isVoid ? 'border-red-200' : 'border-indigo-200'}`}>
            <PaneHeader
              tone="right"
              title={isVoid ? 'Void (mirror of original)' : 'New Corrected Claim'}
              subtitle={isVoid ? 'Will be created as Draft, then submit to ODM' : 'Edit fields below'}
              badge={`Frequency: ${isVoid ? '8 Void' : '7 Correct'}`}
            />
            <div className="bg-white">
              {SUMMARY_FIELDS.map((f) => (
                <ReadOnlyRow
                  key={f.key}
                  label={f.label}
                  value={f.key === 'claim_number'
                    ? <span className="text-slate-400 italic">(auto-generated)</span>
                    : formatValue(f.key, claim[f.key])}
                />
              ))}
              {EDITABLE_FIELDS.map((f) => (
                <FieldEditor
                  key={f.key}
                  field={f}
                  value={form[f.key]}
                  originalValue={claim[f.key] ?? ''}
                  onChange={handleChange}
                  disabled={isVoid}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Diff summary */}
        {!isVoid && (
          <div className={`mt-3 p-3 rounded border ${changedFields.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center gap-2 text-xs">
              {changedFields.length > 0 ? (
                <>
                  <ArrowRight size={14} className="text-amber-700" />
                  <span className="font-medium text-amber-900">
                    {changedFields.length} field{changedFields.length > 1 ? 's' : ''} will change:
                  </span>
                  <span className="text-slate-700 font-mono">{changedFields.join(', ')}</span>
                </>
              ) : (
                <>
                  <Warning size={14} className="text-slate-500" />
                  <span className="text-slate-600">No edits yet — change at least one field, or use "Void" instead if the original was billed in error.</span>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="cv-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (!isVoid && changedFields.length === 0)}
            className={isVoid ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}
            data-testid="cv-confirm-btn"
          >
            {submitting ? 'Creating…' : ctaText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectVoidDialog;
